import React from 'react'
import { Badge, EmptyState } from './ui'
import { Phone, Mail, Building2, Tag, Clock, X } from 'lucide-react'

const norm = (s) => (s || '').toString().trim().toLowerCase()

// Full lead/contact dossier shown when an agent clicks a row in My Work Today.
// Pure presentational: it derives everything from the data already loaded
// (dbContacts for the profile/source, callLogs + activities for the history).
export default function LeadDetail({ company, contact, dbContacts = [], callLogs = [], activities = [], onClose }) {
  const key = norm(company)
  const record = dbContacts.find(c => norm(c.company) === key) || null

  // All interactions for this company, newest first.
  const calls = callLogs
    .filter(c => norm(c.company) === key)
    .sort((a, b) => `${b.call_date || ''} ${b.call_time || ''}`.localeCompare(`${a.call_date || ''} ${a.call_time || ''}`))
  const acts = activities
    .filter(a => norm(a.company_name) === key)
    .sort((a, b) => (b.activity_at || b.created_at || '').localeCompare(a.activity_at || a.created_at || ''))

  const last = calls[0] || null
  const contacts = (record && record.contacts && record.contacts.length)
    ? record.contacts
    : (contact ? [{ name: contact }] : [])

  const sourceLabel = record
    ? (record.source === 'Event'
        ? `Event${record.event_name ? ' — ' + record.event_name : ''}`
        : (record.source || 'Telesales Organic Search'))
    : '—'

  const row = { display: 'flex', padding: '7px 0', borderBottom: '0.5px solid rgba(0,0,0,0.07)', fontSize: 13 }
  const lab = { minWidth: 120, color: '#888', fontWeight: 500 }

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, padding: '1.5rem', width: '100%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 38, height: 38, borderRadius: 10, background: '#E6F1FB', color: '#185FA5', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Building2 size={20} />
            </div>
            <div>
              <div style={{ fontSize: 17, fontWeight: 700, color: '#111' }}>{company || '(tanpa nama)'}</div>
              {record && record.industry && <div style={{ fontSize: 12, color: '#888' }}>{record.industry}</div>}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', display: 'flex' }}><X size={20} /></button>
        </div>

        {/* Meta */}
        <div style={{ marginBottom: 14 }}>
          <div style={row}>
            <span style={lab}><Tag size={12} style={{ verticalAlign: -2, marginRight: 4 }} />Sumber database</span>
            <span style={{ color: '#222' }}>
              {record && record.source === 'Event'
                ? <Badge variant="warning">{sourceLabel}</Badge>
                : <span>{sourceLabel}</span>}
            </span>
          </div>
          {record && (
            <>
              <div style={row}><span style={lab}>Pemilik (agent)</span><span style={{ color: '#222' }}>{record.agent || '—'}{record.created_by_agent && record.created_by_agent !== record.agent ? ` (dibuat oleh ${record.created_by_agent})` : ''}</span></div>
              <div style={row}><span style={lab}>Status</span><span style={{ color: '#222' }}>{record.status || '—'}</span></div>
            </>
          )}
          <div style={row}>
            <span style={lab}><Clock size={12} style={{ verticalAlign: -2, marginRight: 4 }} />Terakhir dihubungi</span>
            <span style={{ color: '#222' }}>
              {last ? `${last.call_date}${last.call_time ? ' ' + last.call_time : ''} — ${last.outcome || '—'}` : 'Belum ada histori call'}
            </span>
          </div>
        </div>

        {/* Contacts */}
        <div style={{ fontSize: 12, fontWeight: 700, color: '#555', margin: '4px 0 6px' }}>Kontak</div>
        {contacts.length === 0 ? <EmptyState>Tidak ada data kontak.</EmptyState> : (
          <div style={{ marginBottom: 14 }}>
            {contacts.map((c, i) => (
              <div key={i} style={{ padding: '7px 10px', background: '#f9f9f7', borderRadius: 8, marginBottom: 6 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#111' }}>{c.name || '(tanpa nama)'}{c.role ? <span style={{ fontWeight: 400, color: '#888' }}> · {c.role}</span> : ''}</div>
                <div style={{ display: 'flex', gap: 14, marginTop: 3, flexWrap: 'wrap' }}>
                  {c.phone && <span style={{ fontSize: 12, color: '#185FA5', display: 'flex', alignItems: 'center', gap: 4 }}><Phone size={11} /> {c.phone}</span>}
                  {c.email && <span style={{ fontSize: 12, color: '#0F6E56', display: 'flex', alignItems: 'center', gap: 4 }}><Mail size={11} /> {c.email}</span>}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* History */}
        <div style={{ fontSize: 12, fontWeight: 700, color: '#555', margin: '4px 0 6px' }}>Histori interaksi ({calls.length})</div>
        {calls.length === 0 && acts.length === 0 ? (
          <EmptyState>Belum ada histori. Lead ini belum pernah dihubungi.</EmptyState>
        ) : (
          <div>
            {calls.map((c, i) => (
              <div key={'c' + (c.id || i)} style={{ display: 'flex', gap: 10, padding: '9px 0', borderBottom: '0.5px solid rgba(0,0,0,0.07)' }}>
                <div style={{ width: 4, alignSelf: 'stretch', borderRadius: 4, background: '#185FA5', flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#111' }}>
                    {c.outcome || 'Call'}{c.call_purpose ? <span style={{ fontWeight: 400, color: '#888' }}> · {c.call_purpose}</span> : ''}
                  </div>
                  {c.notes && <div style={{ fontSize: 12, color: '#555', marginTop: 1 }}>{c.notes}</div>}
                  <div style={{ fontSize: 11, color: '#aaa', marginTop: 2 }}>
                    {c.call_date}{c.call_time ? ' ' + c.call_time : ''} · {c.agent || '—'}
                    {c.bant_score != null ? ` · BANT ${c.bant_score}/4` : ''}
                    {c.meeting_set === 'Yes' ? ' · Meeting set' : ''}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
